import React, {useState, useEffect} from 'react';
import {StyleSheet, ActivityIndicator, View, ScrollView} from 'react-native';
import SlideModal from '@/Atoms/SlideModal';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {formatCurrency, getUserDetails} from '@/Utils/CommonUtils';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import dayjs from 'dayjs';
import useCreateRenewalOrder from '@/Hooks/RenewalApi/useCreateRenewalOrder';
import {SelectedSlot} from '@/Hooks/RenewalApi/useCreateRenewalOrder';
import useGetProfileApi from '@/Hooks/ProfileApi/profileApi';
import useSendRenewalPaymentInitiationWhatsapp from '@/Hooks/RenewalApi/useSendRenewalPaymentInitiationWhatsapp';
import InternationalPaymentMethodSelector, {
  InternationalPaymentMethod,
} from '@/Components/InternationalPaymentMethodSelector';
import ENV from '../../../../environment';

interface PaymentConfirmationModalProps {
  visible: boolean;
  onClose: () => void;
  onPaymentSuccess?: (orderId: string) => void;
  renewalData: any;
  selectedPlanType: 'personal' | 'group' | 'webinar';
  selectedDuration: number;
  selectedSlots: SelectedSlot[];
  selectedCoach?: 'general' | 'international_master' | 'grand_master';
  selectedBatch?: {
    batch_name: string;
    batch_code: string;
    coach_name: string;
    class_timing: string;
    class_days: string;
    start_date: string;
    tournament_id: number;
    level_name: string | null;
  } | null;
  pricingBreakdown: {
    basePrice: number;
    maxBasePrice?: number;
    offerPrice?: number;
    offerDiscountAmount?: number;
    offerDiscountPercentage?: number;
    durationDiscount: number;
    durationDiscountPct: number;
    couponDiscount: number;
    gst: number;
    grandTotal: number;
    monthlyEquivalent: number;
    currency: string;
  } | null;
  appliedCoupon?: any;
  selectedPricing?: {
    level_name?: string | null;
    level_slug?: string | null;
    format_name?: string | null;
    format_slug?: string | null;
  } | null;
  selectedPricingId?: number;
  isNewUser?: boolean;
  is_user_preference_foundation?: boolean;
  user_level_source?: string;
  initialBillingFirstName?: string;
  initialBillingLastName?: string;
  initialBillingLine1?: string;
  initialBillingCountry?: string;
}

const HERO_GRADIENT =
  'linear-gradient(150deg, #FFFDF5 0%, #FAF6EB 60%, #FBEFC9 100%)';
const HERO_BORDER = 'rgba(250,207,71,0.5)';
const FEE_ORANGE = '#E17B21';
const FEE_BG = 'rgba(225,120,70,0.08)';
const FEE_BORDER = 'rgba(225,120,70,0.32)';
const AQUA_DARK = '#4B968D';
const AQUA = '#3DAB9E';
const AQUA_SOFT_BG = 'rgba(61,171,158,0.16)';
const CHIP_BG = 'rgba(255,255,255,0.72)';
const CHIP_BORDER = 'rgba(77,63,55,0.10)';
const DASHED_RULE = 'rgba(77,63,55,0.22)';
const CREAM = '#FAF6EB';
const TEXT_DARK = '#262322';
const TEXT_GREY = '#888280';

const PaymentConfirmationModal: React.FC<PaymentConfirmationModalProps> = ({
  visible,
  onClose,
  onPaymentSuccess,
  renewalData,
  selectedPlanType,
  selectedDuration,
  selectedSlots,
  selectedCoach = 'general',
  selectedBatch = null,
  pricingBreakdown,
  appliedCoupon,
  selectedPricing,
  selectedPricingId,
  isNewUser = false,
  is_user_preference_foundation,
  user_level_source,
  initialBillingFirstName = '',
  initialBillingLastName = '',
  initialBillingLine1 = '',
  initialBillingCountry = '',
}) => {
  const getCoachLabel = (
    coach: 'general' | 'international_master' | 'grand_master',
  ): string => {
    switch (coach) {
      case 'grand_master':
        return 'Grand Masters';
      case 'international_master':
        return 'International Master';
      default:
        return 'Fide Rated Coach';
    }
  };
  const [userEmail, setUserEmail] = useState<string>('');
  const [paymentMethod, setPaymentMethod] =
    useState<InternationalPaymentMethod>();

  const isNonINR =
    pricingBreakdown?.currency && pricingBreakdown.currency !== 'INR';

  const PAYPAL_SURCHARGE_RATE = ENV.PAYPAL_SURCHARGE_RATE || 0;
  const isPayPalMethod =
    paymentMethod === 'diner' ||
    paymentMethod === 'apple_pay' ||
    paymentMethod === 'paypal';

  const surchargedMethodLabel =
    paymentMethod === 'apple_pay'
      ? 'Apple Pay'
      : paymentMethod === 'paypal'
      ? 'PayPal'
      : 'Diners Club';
  const surchargeAmount = isPayPalMethod
    ? Math.round(
        (pricingBreakdown?.grandTotal || 0) * PAYPAL_SURCHARGE_RATE * 100,
      ) / 100
    : 0;
  const displayTotal = isPayPalMethod
    ? Math.round(
        ((pricingBreakdown?.grandTotal || 0) + surchargeAmount) * 100,
      ) / 100
    : pricingBreakdown?.grandTotal;

  const getPlanHeroTitle = () => {
    switch (selectedPlanType) {
      case 'personal':
        return 'Personal 1:1 Coaching';
      case 'group':
        return 'Small Group Coaching';
      case 'webinar':
        return 'Masterclass';
      default:
        return 'Coaching Plan';
    }
  };

  const getPlanSubLabel = () => {
    switch (selectedPlanType) {
      case 'personal':
        return '1-on-1 sessions';
      case 'group':
        return 'Group sessions';
      case 'webinar':
        return 'Live masterclass';
      default:
        return 'Sessions';
    }
  };

  const getLevelLabel = () => {
    const raw =
      selectedPricing?.level_name && is_user_preference_foundation
        ? 'foundation'
        : selectedPricing?.level_name;
    if (!raw) {
      return null;
    }
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  };

  const {mutate: getProfile} = useGetProfileApi(
    data => {
      if (data?.success && data?.data?.user?.[0]?.fields?.email) {
        setUserEmail(data.data.user[0].fields.email);
      }
    },
    error => {
      console.error('Error fetching profile:', error);
    },
  );

  useEffect(() => {
    if (visible) {
      getProfile();
    }
  }, [visible]);

  const {mutate: sendPaymentInitiationWhatsapp} =
    useSendRenewalPaymentInitiationWhatsapp(
      () => {},
      error => {
        console.error('Error sending payment initiation WhatsApp:', error);
      },
    );

  const {mutate: createRenewalOrder, isLoading: isCreatingOrder} =
    useCreateRenewalOrder(
      data => {
        const orderId =
          data?.data?.order_id ||
          data?.order_id ||
          `CSOC-${dayjs().format('YYYY-MM-DD')}-${Math.floor(
            Math.random() * 1000000,
          )}`;

        const paymentLink =
          data?.data?.payment_link || data?.content?.payment_links?.web;

        if (paymentLink) {
          onClose();
          if (typeof window !== 'undefined') {
            window.location.href = paymentLink;
          }
        } else {
          onClose();
          if (onPaymentSuccess) {
            onPaymentSuccess(orderId);
          }
        }
      },
      error => {
        console.error('Error creating renewal order:', error);
      },
    );

  const handlePay = async () => {
    if (!renewalData || !pricingBreakdown) {
      return;
    }

    if (!isNewUser) {
      sendPaymentInitiationWhatsapp({
        plan_type: selectedPlanType,
        duration_months: selectedDuration,
        amount: pricingBreakdown.grandTotal,
        currency: pricingBreakdown?.currency || 'INR',
      });
    }

    const user = renewalData.user;
    const previous_order_id = isNewUser
      ? undefined
      : renewalData?.current_subscription?.order_id;
    const returnUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}/SuccessPage`
        : 'https://learn.circlechess.com/SuccessPage';

    const email = userEmail || user.email || `${user.mobile}@circlechess.com`;
    let userDetails = await getUserDetails();
    userDetails = JSON.parse(userDetails);
    const player_id = userDetails?.player;
    const selectedPricingEntry = selectedPricingId
      ? renewalData?.pricing?.find((p: any) => p.id === selectedPricingId)
      : null;

    createRenewalOrder({
      tournament_id:
        selectedPricingEntry?.tournament_id ||
        renewalData.recommended_next_level?.tournament_id ||
        renewalData.current_subscription?.tournament_id,
      plan_type: selectedPlanType,
      duration_months: selectedDuration,
      coupon_code: appliedCoupon?.code,
      selectedSlots: selectedSlots,
      selectedBatch: selectedBatch,
      customer_id: user.id,
      player_id: player_id,
      mobile_number: user.mobile,
      prev_order_id: previous_order_id,
      email: email,
      return_url: returnUrl,
      timezone: 'Asia/Kolkata',
      amount: pricingBreakdown.grandTotal,
      currency: pricingBreakdown.currency,
      selected_pricing_id: selectedPricingId,
      category_str: `${
        pricingBreakdown?.currency || 'INR'
      } /${selectedDuration}-Months`,
      isNewUser,
      is_user_preference_foundation,
      user_level_source,
      ...(isNonINR
        ? {
            billing_address_first_name: initialBillingFirstName.trim(),
            billing_address_last_name: initialBillingLastName.trim(),
            billing_address_line1: initialBillingLine1.trim(),
            billing_address_country: initialBillingCountry.trim(),
            payment_method_type: paymentMethod,
          }
        : {}),
    });
  };

  const levelLabel = getLevelLabel();
  const currency = pricingBreakdown?.currency || 'INR';
  const monthlyForHero = pricingBreakdown
    ? pricingBreakdown.monthlyEquivalent
    : 0;
  const planTotalForHero = pricingBreakdown ? pricingBreakdown.grandTotal : 0;
  const baseForHero = pricingBreakdown
    ? pricingBreakdown.maxBasePrice || pricingBreakdown.basePrice
    : 0;
  const hasSavePill =
    !!pricingBreakdown && pricingBreakdown.durationDiscountPct > 0;

  return (
    <SlideModal
      visible={visible}
      onClose={onClose}
      contentPadding={0}
      footerStyle={{width: '100%', backgroundColor: CCColors.White}}
      type="center"
      modalHeaderTitle={
        <Box flexDirection="row" alignItems="center" gap={10}>
          <Box
            // @ts-ignore web-only inline style
            style={{
              width: 26,
              height: 26,
              borderRadius: 999,
              backgroundColor: AQUA_SOFT_BG,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <MaterialCommunityIcons name="check" size={14} color={AQUA} />
          </Box>
          <Box>
            <CCText
              fontSize={FontSizes[10]}
              fontWeight="bold"
              color={CCColors.TextColor.GreyText}
              style={{letterSpacing: 1, textTransform: 'uppercase'}}>
              Coaching Plan · Checkout
            </CCText>
            <CCText
              fontSize={FontSizes[18]}
              fontWeight="bold"
              color={TEXT_DARK}>
              Confirm Your Selection
            </CCText>
          </Box>
        </Box>
      }
      width={0.35}
      minWidth={460}
      footerComponent={
        <View style={styles.bottomButtonWrapper}>
          <CCButton
            onPress={onClose}
            style={[styles.button, styles.cancelButton]}
            contentContainerStyle={styles.buttonContent}>
            <CCText
              fontSize={FontSizes[14]}
              fontWeight="bold"
              color={CCColors.TextColor.Dark}>
              Cancel
            </CCText>
          </CCButton>
          <CCButton
            onPress={handlePay}
            disabled={
              isCreatingOrder ||
              !pricingBreakdown ||
              pricingBreakdown.grandTotal <= 0 ||
              (isNonINR &&
                (!initialBillingFirstName.trim() ||
                  !initialBillingLastName.trim() ||
                  !initialBillingLine1.trim() ||
                  !initialBillingCountry.trim()))
            }
            style={[styles.button, styles.payButton]}
            contentContainerStyle={styles.buttonContent}>
            {isCreatingOrder ? (
              <Box flexDirection="row" alignItems="center" gap={8}>
                <ActivityIndicator size="small" color={CCColors.White} />
                <CCText
                  fontSize={FontSizes[14]}
                  fontWeight="bold"
                  color={CCColors.White}>
                  Processing...
                </CCText>
              </Box>
            ) : (
              <Box flexDirection="row" alignItems="center" gap={8}>
                <MaterialCommunityIcons
                  name="lock"
                  size={16}
                  color={CCColors.TextColor.Dark}
                />
                <CCText
                  fontSize={FontSizes[14]}
                  fontWeight="bold"
                  color={CCColors.TextColor.Dark}>
                  Pay {formatCurrency(displayTotal ?? 0, currency)}
                </CCText>
              </Box>
            )}
          </CCButton>
        </View>
      }
      customStyle={{
        width: 480,
        maxWidth: 480,
        maxHeight: '85vh',
      }}
      showCloseIcon={true}>
      <ScrollView
        style={{flex: 1}}
        contentContainerStyle={{
          paddingHorizontal: 20,
          paddingTop: 8,
          paddingBottom: 110,
        }}
        showsVerticalScrollIndicator={false}>
        {!pricingBreakdown ? (
          <Box alignItems="center" paddingVertical={40}>
            <CCText
              fontSize={FontSizes[16]}
              color={CCColors.TextColor.Dark}
              marginBottom={10}>
              Unable to load pricing information
            </CCText>
            <CCText
              fontSize={FontSizes[14]}
              color={CCColors.TextColor.Grey}
              textAlign="center">
              Please go back and select a plan to continue
            </CCText>
            <CCButton
              onPress={onClose}
              style={[styles.button, styles.cancelButton, {marginTop: 20}]}
              contentContainerStyle={styles.buttonContent}>
              <CCText
                fontSize={FontSizes[14]}
                fontWeight="bold"
                color={CCColors.TextColor.Dark}>
                Go Back
              </CCText>
            </CCButton>
          </Box>
        ) : (
          <>
            {/* ============== COMPACT HERO ============== */}
            <Box
              marginBottom={10}
              style={
                {
                  position: 'relative',
                  padding: 14,
                  background: HERO_GRADIENT,
                  border: `1px solid ${HERO_BORDER}`,
                  borderRadius: 16,
                  overflow: 'hidden',
                } as any
              }>
              {/* faint chessboard pattern in the corner */}
              <View
                pointerEvents="none"
                style={styles.heroBoardPattern}
                aria-hidden>
                {Array.from({length: 64}).map((_, i) => {
                  const x = i % 8;
                  const y = Math.floor(i / 8);
                  if ((x + y) % 2 !== 0) {
                    return null;
                  }
                  return (
                    <View
                      key={i}
                      style={{
                        position: 'absolute',
                        left: x * 9,
                        top: y * 9,
                        width: 9,
                        height: 9,
                        backgroundColor: '#D1AB41',
                        opacity: 0.5,
                      }}
                    />
                  );
                })}
              </View>

              <Box
                flexDirection="row"
                justifyContent="space-between"
                alignItems="flex-start"
                gap={12}>
                <Box flex={1}>
                  <Box flexDirection="row" alignItems="center" gap={6}>
                    <MaterialCommunityIcons
                      name="chess-knight"
                      size={12}
                      color={CCColors.BrownShade2 || '#806234'}
                    />
                    <CCText
                      fontSize={FontSizes[10]}
                      fontWeight="bold"
                      color={CCColors.BrownShade2 || '#806234'}
                      style={{letterSpacing: 1, textTransform: 'uppercase'}}>
                      Your Plan
                    </CCText>
                  </Box>
                  <CCText
                    fontSize={FontSizes[20] || 20}
                    fontWeight="bold"
                    color={TEXT_DARK}
                    style={{marginTop: 2, lineHeight: 24}}>
                    {getPlanHeroTitle()}
                  </CCText>
                  <CCText
                    fontSize={FontSizes[12]}
                    color="#6B584D"
                    style={{marginTop: 2}}>
                    with a {getCoachLabel(selectedCoach)}
                  </CCText>
                </Box>

                <Box alignItems="flex-end">
                  <Box flexDirection="row" alignItems="baseline">
                    <CCText fontSize={26} fontWeight="bold" color={TEXT_DARK}>
                      {formatCurrency(monthlyForHero, currency)}
                    </CCText>
                    <CCText
                      fontSize={FontSizes[12]}
                      fontWeight="semibold"
                      color={TEXT_GREY}
                      style={{marginLeft: 3}}>
                      /mo
                    </CCText>
                  </Box>
                  {hasSavePill && (
                    <Box
                      flexDirection="row"
                      alignItems="center"
                      gap={4}
                      style={
                        {
                          paddingHorizontal: 8,
                          paddingVertical: 3,
                          backgroundColor: AQUA_DARK,
                          borderRadius: 999,
                          marginTop: 4,
                        } as any
                      }>
                      <MaterialCommunityIcons
                        name="check"
                        size={10}
                        color="#fff"
                      />
                      <CCText
                        fontSize={FontSizes[10]}
                        fontWeight="bold"
                        color={CCColors.White}
                        style={{letterSpacing: 0.4}}>
                        Save {pricingBreakdown.durationDiscountPct}%
                      </CCText>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Chips row — compact, single line */}
              <Box flexDirection="row" flexWrap="wrap" gap={6} marginTop={10}>
                {levelLabel && (
                  <Box
                    flexDirection="row"
                    alignItems="center"
                    gap={4}
                    style={
                      {
                        paddingHorizontal: 9,
                        paddingVertical: 3,
                        backgroundColor: CHIP_BG,
                        border: `1px solid ${CHIP_BORDER}`,
                        borderRadius: 999,
                      } as any
                    }>
                    <MaterialCommunityIcons
                      name="signal"
                      size={11}
                      color={TEXT_DARK}
                    />
                    <CCText
                      fontSize={FontSizes[11]}
                      fontWeight="semibold"
                      color={TEXT_DARK}>
                      {levelLabel}
                    </CCText>
                  </Box>
                )}
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap={4}
                  style={
                    {
                      paddingHorizontal: 9,
                      paddingVertical: 3,
                      backgroundColor: CHIP_BG,
                      border: `1px solid ${CHIP_BORDER}`,
                      borderRadius: 999,
                    } as any
                  }>
                  <MaterialCommunityIcons
                    name="calendar-blank"
                    size={11}
                    color={TEXT_DARK}
                  />
                  <CCText
                    fontSize={FontSizes[11]}
                    fontWeight="semibold"
                    color={TEXT_DARK}>
                    {selectedDuration} Months
                  </CCText>
                </Box>
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap={4}
                  style={
                    {
                      paddingHorizontal: 9,
                      paddingVertical: 3,
                      backgroundColor: CHIP_BG,
                      border: `1px solid ${CHIP_BORDER}`,
                      borderRadius: 999,
                    } as any
                  }>
                  <MaterialCommunityIcons
                    name="account-multiple"
                    size={11}
                    color={TEXT_DARK}
                  />
                  <CCText
                    fontSize={FontSizes[11]}
                    fontWeight="semibold"
                    color={TEXT_DARK}>
                    {getPlanSubLabel()}
                  </CCText>
                </Box>
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap={4}
                  style={
                    {
                      paddingHorizontal: 9,
                      paddingVertical: 3,
                      backgroundColor: CHIP_BG,
                      border: `1px solid ${CHIP_BORDER}`,
                      borderRadius: 999,
                    } as any
                  }>
                  <CCText fontSize={FontSizes[11]} color={TEXT_GREY}>
                    Plan total
                  </CCText>
                  <CCText
                    fontSize={FontSizes[11]}
                    fontWeight="semibold"
                    color={TEXT_DARK}>
                    {formatCurrency(planTotalForHero, currency)}
                  </CCText>
                </Box>
              </Box>
            </Box>

            {/* Selected Batch (group) — compact single line */}
            {selectedPlanType === 'group' && selectedBatch && (
              <Box
                flexDirection="row"
                alignItems="center"
                flexWrap="wrap"
                gap={8}
                padding={8}
                marginBottom={8}
                backgroundColor={CCColors.Primary.lightBrown}
                borderRadius={10}
                borderWidth={1}
                borderColor={CCColors.Primary.Default}>
                <MaterialCommunityIcons
                  name="account-group"
                  size={14}
                  color={CCColors.TextColor.Dark}
                />
                <CCText
                  fontSize={FontSizes[11]}
                  fontWeight="semibold"
                  color={CCColors.TextColor.Dark}
                  numberOfLines={1}>
                  {selectedBatch.batch_name}
                </CCText>
                {selectedBatch.class_days && (
                  <CCText
                    fontSize={FontSizes[11]}
                    color={CCColors.TextColor.Grey}>
                    ·{' '}
                    {selectedBatch.class_days
                      .split(',')
                      .map(d => {
                        const dayMap: {[key: string]: string} = {
                          '1': 'Mon',
                          '2': 'Tue',
                          '3': 'Wed',
                          '4': 'Thu',
                          '5': 'Fri',
                          '6': 'Sat',
                          '7': 'Sun',
                        };
                        return dayMap[d.trim()] || d.trim();
                      })
                      .join(', ')}
                  </CCText>
                )}
                {selectedBatch.start_date && (
                  <CCText
                    fontSize={FontSizes[11]}
                    color={CCColors.TextColor.Grey}>
                    · Starts {dayjs(selectedBatch.start_date).format('MMM D')}
                  </CCText>
                )}
              </Box>
            )}

            {/* Slots — compact chip row (handles both group & personal/webinar) */}
            {selectedSlots.length > 0 && (
              <Box
                flexDirection="row"
                flexWrap="wrap"
                alignItems="center"
                gap={6}
                marginBottom={8}>
                <CCText
                  fontSize={FontSizes[11]}
                  color={CCColors.TextColor.GreyText}>
                  {selectedPlanType === 'group' ? 'Custom' : 'Selected'} slots (
                  {selectedSlots.length}):
                </CCText>
                {selectedSlots.slice(0, 4).map((slot, index) => {
                  const slotDate = dayjs(slot.date);
                  const dayName = slotDate.format('ddd');
                  const timeStr = slot.time.includes('T')
                    ? dayjs(slot.time).format('h:mm A')
                    : dayjs(`2000-01-01 ${slot.time}`).format('h:mm A');
                  return (
                    <Box
                      key={`${slot.date}_${slot.time}_${index}`}
                      paddingHorizontal={8}
                      paddingVertical={3}
                      backgroundColor={CCColors.Primary.lightBrown}
                      borderRadius={6}
                      borderWidth={1}
                      borderColor={CCColors.Primary.Default}>
                      <CCText
                        fontSize={FontSizes[11]}
                        color={CCColors.TextColor.Dark}
                        fontWeight="semibold">
                        {dayName} {timeStr}
                      </CCText>
                    </Box>
                  );
                })}
                {selectedSlots.length > 4 && (
                  <CCText
                    fontSize={FontSizes[11]}
                    color={CCColors.TextColor.GreyText}>
                    +{selectedSlots.length - 4} more
                  </CCText>
                )}
              </Box>
            )}

            {/* ============== COMPACT BREAKDOWN ============== */}
            <Box
              marginBottom={10}
              padding={12}
              backgroundColor={CREAM}
              borderRadius={12}>
              <Box
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                paddingVertical={2}>
                <CCText fontSize={FontSizes[12]} color="#4D3F37">
                  Base fee
                  {currency === 'INR' ? ' (incl. GST)' : ''}
                </CCText>
                <Box flexDirection="row" alignItems="center" gap={6}>
                  <CCText
                    fontSize={FontSizes[12]}
                    fontWeight="semibold"
                    color={TEXT_DARK}>
                    {formatCurrency(baseForHero, currency)}
                  </CCText>
                  {pricingBreakdown.offerDiscountPercentage &&
                  pricingBreakdown.offerDiscountPercentage > 0 ? (
                    <Box
                      backgroundColor={CCColors.Orange}
                      paddingHorizontal={5}
                      paddingVertical={1}
                      borderRadius={4}>
                      <CCText
                        fontSize={FontSizes[9] || 9}
                        color={CCColors.White}
                        fontWeight="bold">
                        OFFER
                      </CCText>
                    </Box>
                  ) : null}
                </Box>
              </Box>

              {pricingBreakdown.durationDiscount > 0 && (
                <Box
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  paddingVertical={2}>
                  <CCText fontSize={FontSizes[12]} color={AQUA_DARK}>
                    Duration discount
                    {pricingBreakdown.durationDiscountPct > 0
                      ? ` (${pricingBreakdown.durationDiscountPct}% off)`
                      : ''}
                  </CCText>
                  <CCText
                    fontSize={FontSizes[12]}
                    fontWeight="semibold"
                    color={AQUA_DARK}>
                    −
                    {formatCurrency(
                      pricingBreakdown.durationDiscount,
                      currency,
                    )}
                  </CCText>
                </Box>
              )}

              {pricingBreakdown.couponDiscount > 0 && (
                <Box
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  paddingVertical={2}>
                  <CCText fontSize={FontSizes[12]} color={AQUA_DARK}>
                    Coupon
                    {appliedCoupon?.code ? ` · ${appliedCoupon.code}` : ''}
                  </CCText>
                  <CCText
                    fontSize={FontSizes[12]}
                    fontWeight="semibold"
                    color={AQUA_DARK}>
                    −{formatCurrency(pricingBreakdown.couponDiscount, currency)}
                  </CCText>
                </Box>
              )}

              {isPayPalMethod && surchargeAmount > 0 && (
                <Box
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  paddingVertical={2}>
                  <Box flexDirection="row" alignItems="center" gap={4}>
                    <MaterialCommunityIcons
                      name="information-outline"
                      size={11}
                      color={FEE_ORANGE}
                    />
                    <CCText fontSize={FontSizes[12]} color={FEE_ORANGE}>
                      {surchargedMethodLabel} fee (
                      {(PAYPAL_SURCHARGE_RATE * 100).toFixed(1)}%)
                    </CCText>
                  </Box>
                  <CCText
                    fontSize={FontSizes[12]}
                    fontWeight="semibold"
                    color={FEE_ORANGE}>
                    +{formatCurrency(surchargeAmount, currency)}
                  </CCText>
                </Box>
              )}

              <View
                style={
                  {
                    height: 0,
                    borderTopWidth: 1,
                    borderTopStyle: 'dashed',
                    borderTopColor: DASHED_RULE,
                    marginVertical: 4,
                  } as any
                }
              />

              <Box
                flexDirection="row"
                justifyContent="space-between"
                alignItems="baseline"
                paddingVertical={2}>
                <Box flexDirection="row" alignItems="baseline" gap={6}>
                  <CCText
                    fontSize={FontSizes[13]}
                    fontWeight="bold"
                    color={TEXT_DARK}>
                    You pay today
                  </CCText>
                  <CCText fontSize={FontSizes[10]} color={TEXT_GREY}>
                    (
                    {formatCurrency(
                      pricingBreakdown.monthlyEquivalent,
                      currency,
                    )}
                    /mo)
                  </CCText>
                </Box>
                <CCText
                  fontSize={FontSizes[18] || 18}
                  fontWeight="bold"
                  color={TEXT_DARK}>
                  {formatCurrency(displayTotal ?? 0, currency)}
                </CCText>
              </Box>
            </Box>

            {/* International payment method picker (existing behavior) */}
            {isNonINR && (
              <InternationalPaymentMethodSelector
                value={paymentMethod}
                onChange={setPaymentMethod}
              />
            )}

            {/* Surcharge notice strip — single compact line */}
            {isPayPalMethod && surchargeAmount > 0 && (
              <Box
                flexDirection="row"
                alignItems="center"
                gap={8}
                padding={8}
                marginBottom={8}
                style={
                  {
                    backgroundColor: FEE_BG,
                    border: `1px solid ${FEE_BORDER}`,
                    borderRadius: 10,
                  } as any
                }>
                <MaterialCommunityIcons
                  name="information"
                  size={14}
                  color={FEE_ORANGE}
                />
                <Box flex={1}>
                  <CCText
                    fontSize={FontSizes[11]}
                    color={CCColors.TextColor.Dark}>
                    <CCText
                      fontSize={FontSizes[11]}
                      fontWeight="bold"
                      color={TEXT_DARK}>
                      {surchargedMethodLabel}
                    </CCText>{' '}
                    {paymentMethod === 'paypal'
                      ? 'checkout adds'
                      : 'routes via PayPal · adds'}{' '}
                    {(PAYPAL_SURCHARGE_RATE * 100).toFixed(1)}% fee.{' '}
                    <CCText
                      fontSize={FontSizes[11]}
                      fontWeight="semibold"
                      color={AQUA_DARK}
                      onPress={() => setPaymentMethod(undefined)}
                      style={
                        {
                          textDecorationLine: 'underline',
                          cursor: 'pointer',
                        } as any
                      }>
                      Use default
                    </CCText>
                  </CCText>
                </Box>
              </Box>
            )}

            {/* Timing notice — single compact line */}
            {selectedSlots.length > 0 && (
              <Box
                flexDirection="row"
                alignItems="center"
                gap={8}
                padding={8}
                backgroundColor={CCColors.Orange + '15'}
                borderRadius={10}
                borderWidth={1}
                borderColor={CCColors.Orange}
                marginBottom={8}>
                <MaterialCommunityIcons
                  name="information"
                  size={14}
                  color={CCColors.Orange}
                />
                <CCText
                  fontSize={FontSizes[11]}
                  color={CCColors.TextColor.Dark}
                  style={{flex: 1}}>
                  Selected time slots may shift — we'll reach out to confirm the
                  final schedule.
                </CCText>
              </Box>
            )}
          </>
        )}
      </ScrollView>
    </SlideModal>
  );
};

const styles = StyleSheet.create({
  button: {
    flex: 1,
    borderRadius: 12,
    paddingVertical: 14,
  },
  cancelButton: {
    backgroundColor: CCColors.Grey.Divider200,
  },
  bottomButtonWrapper: {
    padding: 10,
    background: CCColors.White,
    width: '100%',
    left: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  payButton: {
    backgroundColor: CCColors.Primary.Default,
  },
  buttonContent: {
    padding: 0,
  },
  heroBoardPattern: {
    position: 'absolute',
    top: -14,
    right: -14,
    width: 96,
    height: 96,
    opacity: 1,
  },
});

export default PaymentConfirmationModal;
